import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, FileText, Clock, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import { getMyRequests } from '../../../services/studentRequestsService';
import { StudentRequest, RequestStatus } from '../../../types/student-requests.types';

const RequestsDashboardPage = () => {
  const [requests, setRequests] = useState<StudentRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    loadRequests();
  }, []);

  const loadRequests = async () => {
    try {
      setLoading(true);
      const response = await getMyRequests({});
      setRequests(response.data);
    } catch (error) {
      console.error('Error loading requests:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status: RequestStatus) => {
    // Simplified 3-state system for approved requests
    if (status === RequestStatus.APPROVED) {
      return (
        <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-sky-100 text-sky-700">
          <CheckCircle className="w-3 h-3" />
          Approuvée
        </span>
      );
    }

    if ([RequestStatus.GENERATED, RequestStatus.DOWNLOADED_FOR_PRINT, RequestStatus.PRINTED].includes(status)) {
      return (
        <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
          <CheckCircle className="w-3 h-3" />
          Prête à collecter
        </span>
      );
    }

    if (status === RequestStatus.DELIVERED) {
      return (
        <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
          <CheckCircle className="w-3 h-3" />
          Délivrée
        </span>
      );
    }

    // For other statuses (pending validation, rejected, cancelled)
    const statusConfig: Record<RequestStatus, { label: string; className: string; icon: typeof Clock }> = {
      [RequestStatus.DRAFT]: { label: 'Brouillon', className: 'bg-gray-100 text-gray-800', icon: Clock },
      [RequestStatus.PENDING_FINANCE]: { label: 'En attente - Finance', className: 'bg-yellow-100 text-yellow-800', icon: Clock },
      [RequestStatus.PENDING_ACADEMIC]: { label: 'En attente - Académique', className: 'bg-blue-100 text-blue-800', icon: Clock },
      [RequestStatus.FINANCE_APPROVED]: { label: 'Finance approuvée', className: 'bg-green-100 text-green-800', icon: CheckCircle },
      [RequestStatus.ACADEMIC_APPROVED]: { label: 'Académique approuvée', className: 'bg-green-100 text-green-800', icon: CheckCircle },
      [RequestStatus.APPROVED]: { label: 'Approuvée', className: 'bg-green-100 text-green-800', icon: CheckCircle },
      [RequestStatus.GENERATED]: { label: 'Générée', className: 'bg-emerald-100 text-emerald-800', icon: CheckCircle },
      [RequestStatus.DOWNLOADED_FOR_PRINT]: { label: 'Téléchargée', className: 'bg-emerald-100 text-emerald-800', icon: CheckCircle },
      [RequestStatus.PRINTED]: { label: 'Imprimée', className: 'bg-emerald-100 text-emerald-800', icon: CheckCircle },
      [RequestStatus.DELIVERED]: { label: 'Délivrée', className: 'bg-teal-100 text-teal-800', icon: CheckCircle },
      [RequestStatus.REJECTED_FINANCE]: { label: 'Rejetée - Finance', className: 'bg-red-100 text-red-800', icon: XCircle },
      [RequestStatus.REJECTED_ACADEMIC]: { label: 'Rejetée - Académique', className: 'bg-red-100 text-red-800', icon: XCircle },
      [RequestStatus.CANCELLED]: { label: 'Annulée', className: 'bg-gray-100 text-gray-800', icon: XCircle },
    };

    const config = statusConfig[status] || { label: status, className: 'bg-gray-100 text-gray-800', icon: AlertCircle };
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${config.className}`}>
        <Icon className="w-3 h-3" />
        {config.label}
      </span>
    );
  };

  const getTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      PRE_INSCRIPTION: 'Attestation d\'Inscription',
      ATTESTATION_SCOLARITE: 'Attestation de Scolarité',
      RELEVE_NOTES_ANNUEL: 'Relevé de Notes Annuel',
      ATTESTATION_REUSSITE: 'Attestation de Réussite',
      ATTESTATION_INSCRIPTION: 'Attestation d\'Inscription',
      CERTIFICAT_ASSIDUITE: 'Certificat d\'Assiduité',
      RELEVE_NOTES_SEMESTRIEL: 'Relevé de Notes Semestriel',
      ATTESTATION_STAGE: 'Attestation de Stage',
      CONVENTION_STAGE: 'Convention de Stage',
    };
    return labels[type] || type;
  };

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Mes Demandes</h1>
          <p className="text-gray-600 mt-1">Gérez vos demandes d'attestations</p>
        </div>
        <button
          onClick={() => navigate('new')}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
        >
          <Plus className="w-5 h-5" />
          Nouvelle Demande
        </button>
      </div>

      {/* Requests Table */}
      {loading ? (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <p className="text-gray-600 mt-4">Chargement...</p>
        </div>
      ) : requests.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
          <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Aucune demande</h3>
          <p className="text-gray-600 mb-6">Vous n'avez pas encore fait de demande d'attestation.</p>
          <button
            onClick={() => navigate('new')}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
          >
            <Plus className="w-5 h-5" />
            Créer votre première demande
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Référence
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Statut
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Année Académique
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date de Création
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {requests.map((request) => (
                <tr key={request.id} className="hover:bg-gray-50 transition cursor-pointer" onClick={() => navigate(`${request.id}`)}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{request.referenceNumber}</div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900">{getTypeLabel(request.requestType)}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getStatusBadge(request.status)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-600">{request.academicYear?.name}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-600">
                      {new Date(request.createdAt).toLocaleDateString('fr-FR')}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`${request.id}`);
                      }}
                      className="text-blue-600 hover:text-blue-900"
                    >
                      Voir détails
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default RequestsDashboardPage;
